<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

import { api } from "@/api/client";
import { describeError as friendlyError } from "@/utils/errors";
import type { NxProfile, NxProxy, SessionEngine, SessionTarget } from "@/api/types";
import { useMachinesStore } from "@/stores/machines";
import AgentFilterBar from "@/components/AgentFilterBar.vue";
import { useAgentFilter } from "@/composables/useAgentFilter";
import DirPicker from "@/components/DirPicker.vue";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const machines = useMachinesStore();

const target = ref<SessionTarget>("agent");
const agentId = ref<string>("");
const nxId = ref<string>("");
const engine = ref<SessionEngine>("shell");
const tool = ref<"codex" | "claude">("codex");
const profile = ref<string>("");
const proxy = ref<string>("");
const name = ref("");
const workingDir = ref("");

// §3.1 SSH relay: optionally ssh from the chosen agent (A) to another agent (B).
const relayTarget = ref<string>("");
const relayPort = ref<number>(22);
const sshUser = ref<string>("");
// Other online agents in the same fleet that can be ssh'd into from A.
const relayTargets = computed(() =>
  machines.agents.filter((a) => a.id !== agentId.value && a.presence === "online"),
);

const profiles = ref<NxProfile[]>([]);
const proxies = ref<NxProxy[]>([]);

const submitting = ref(false);
const errorMessage = ref<string | null>(null);

// Per-agent advertised capabilities (id → wire strings). Drives which session
// engines are offered: a Windows agent has no tmux/zellij; a Win7/Server-2008R2
// agent (relay-only Level 0) has no shell at all. Fetched from /api/agents.
const agentCaps = ref<Record<string, string[]>>({});
async function loadAgentCaps() {
  try {
    const r = await api.agents.list();
    const map: Record<string, string[]> = {};
    for (const a of r.agents) map[a.id] = a.capabilities ?? [];
    agentCaps.value = map;
  } catch {
    // Non-fatal: fall back to offering all engines (server still enforces caps).
  }
}

const ALL_ENGINES: SessionEngine[] = ["shell", "tmux", "zellij"];
const engineOptions = computed<SessionEngine[]>(() => {
  if (target.value !== "agent") return ALL_ENGINES;
  const caps = agentCaps.value[agentId.value];
  if (caps === undefined) return ALL_ENGINES; // unknown yet — don't over-restrict
  const hasShell = caps.includes("shell") && caps.includes("terminal");
  const opts: SessionEngine[] = [];
  if (hasShell) opts.push("shell");
  if (hasShell && caps.includes("mux:tmux")) opts.push("tmux");
  if (hasShell && caps.includes("mux:zellij")) opts.push("zellij");
  return opts;
});
// Agent that can't host any direct session engine (legacy Windows): the only
// way in is the SSH relay.
const relayOnlyAgent = computed(
  () =>
    target.value === "agent" &&
    agentId.value !== "" &&
    agentCaps.value[agentId.value] !== undefined &&
    engineOptions.value.length === 0,
);
watch(
  engineOptions,
  (opts) => {
    if (opts.length && !opts.includes(engine.value)) engine.value = opts[0];
  },
  { immediate: true },
);

onMounted(async () => {
  await machines.start();
  void loadAgentCaps();
  const q = route.query;
  if (typeof q.nx_endpoint_id === "string") {
    target.value = "nx";
    nxId.value = q.nx_endpoint_id;
    await loadNxFixtures();
  } else if (typeof q.agent_id === "string") {
    target.value = "agent";
    agentId.value = q.agent_id;
  } else if (agents.value.length > 0) {
    agentId.value = agents.value[0].id;
  }
});

onBeforeUnmount(() => {
  machines.stop();
});

const agents = computed(() => machines.agents);
// Picker filter (search/tags/online) — local state, not URL-synced.
const filter = useAgentFilter(agents);

// We piggyback on /api/nx-endpoints for the NX list since the machines
// store currently tracks agents only. Refresh inline.
interface NxOption {
  id: string;
  hostname: string;
  display_name: string | null;
  presence?: string;
}
const nxEndpoints = ref<NxOption[]>([]);

async function loadNxList() {
  try {
    const resp = await api.nxEndpoints.list();
    nxEndpoints.value = resp.nx_endpoints;
    if (!nxId.value && nxEndpoints.value.length > 0) {
      nxId.value = nxEndpoints.value[0].id;
    }
  } catch (e) {
    errorMessage.value = describeError(e);
  }
}

watch(target, async (value) => {
  errorMessage.value = null;
  if (value === "nx") {
    if (nxEndpoints.value.length === 0) await loadNxList();
    if (nxId.value) await loadNxFixtures();
    // NX tool sessions default to a mux for recovery (§9.9); tmux & zellij
    // both supported now, so just nudge the bare-shell default to tmux.
    if (engine.value === "shell") engine.value = "tmux";
  } else {
    engine.value = "shell";
  }
});

watch(nxId, async (id) => {
  if (target.value === "nx" && id) await loadNxFixtures();
});

async function loadNxFixtures() {
  if (!nxId.value) return;
  try {
    const [p, x] = await Promise.all([
      api.nx.profileList(nxId.value, tool.value),
      api.nx.proxyList(nxId.value),
    ]);
    profiles.value = p.profiles;
    proxies.value = x.proxies;
    if (!profile.value && profiles.value.length > 0) {
      profile.value = profiles.value[0].name;
    }
  } catch (e) {
    errorMessage.value = describeError(e);
  }
}

watch(tool, () => {
  if (target.value === "nx" && nxId.value) void loadNxFixtures();
});

const canSubmit = computed(() => {
  if (submitting.value) return false;
  if (target.value === "agent") {
    const agent = agents.value.find((a) => a.id === agentId.value);
    if (!agent || agent.presence !== "online" || agent.status !== "enabled") return false;
    // Legacy/relay-only agent: a direct session is impossible — require a relay.
    if (relayOnlyAgent.value && !relayTarget.value) return false;
    return true;
  }
  return nxId.value !== "" && profile.value !== "";
});

async function submit() {
  errorMessage.value = null;
  submitting.value = true;
  try {
    if (target.value === "agent") {
      const relay = relayTarget.value || undefined;
      const session = await api.sessions.start(agentId.value, {
        engine: engine.value,
        name: name.value.trim() || undefined,
        working_dir: relay ? undefined : workingDir.value.trim() || undefined,
        relay_target: relay,
        relay_port: relay ? relayPort.value : undefined,
        ssh_user: relay ? sshUser.value.trim() || undefined : undefined,
      });
      await router.push({ name: "session", params: { sessionId: session.id } });
    } else {
      const session = await api.nx.startSession(nxId.value, {
        engine: engine.value,
        tool: tool.value,
        profile: profile.value,
        proxy: proxy.value || undefined,
        name: name.value.trim() || undefined,
        working_dir: workingDir.value.trim() || undefined,
      });
      await router.push({ name: "session", params: { sessionId: session.id } });
    }
  } catch (err) {
    errorMessage.value = describeError(err);
  } finally {
    submitting.value = false;
  }
}

function describeError(err: unknown): string {
  return friendlyError(err, t);
}
</script>

<template>
  <section class="new">
    <h1>{{ t("new.title") }}</h1>
    <form class="new__form" @submit.prevent="submit">
      <fieldset class="new__target">
        <legend>{{ t("new.target") }}</legend>
        <label>
          <input v-model="target" type="radio" value="agent" />
          {{ t("new.target_agent") }}
        </label>
        <label>
          <input v-model="target" type="radio" value="nx" />
          {{ t("new.target_nx") }}
        </label>
      </fieldset>

      <template v-if="target === 'agent'">
        <AgentFilterBar v-if="agents.length > 1" :filter="filter" />
        <label>
          <span>{{ t("new.agent") }}</span>
          <select v-model="agentId" :disabled="submitting">
            <option v-if="agents.length === 0" value="">{{ t("new.no_agents") }}</option>
            <option v-for="a in filter.filtered" :key="a.id" :value="a.id">
              {{ a.display_name || a.hostname }} ({{ a.login }})
              <template v-if="a.presence !== 'online'"> — {{ t("home.offline") }}</template>
            </option>
          </select>
        </label>

        <label>
          <span>{{ t("new.relay_target") }}</span>
          <select v-model="relayTarget" :disabled="submitting">
            <option value="">{{ t("new.relay_none") }}</option>
            <option v-for="b in relayTargets" :key="b.id" :value="b.id">
              {{ b.display_name || b.hostname }} ({{ b.login }})
            </option>
          </select>
        </label>
        <template v-if="relayTarget">
          <label>
            <span>{{ t("new.ssh_user") }}</span>
            <input
              v-model="sshUser"
              :disabled="submitting"
              :placeholder="t('new.ssh_user_placeholder')"
            />
          </label>
          <label>
            <span>{{ t("new.relay_port") }}</span>
            <input
              v-model.number="relayPort"
              type="number"
              min="1"
              max="65535"
              :disabled="submitting"
            />
          </label>
          <p class="new__hint">{{ t("new.relay_hint") }}</p>
        </template>
      </template>

      <template v-else>
        <label>
          <span>{{ t("new.nx_endpoint") }}</span>
          <select v-model="nxId" :disabled="submitting">
            <option v-if="nxEndpoints.length === 0" value="">
              {{ t("new.no_nx_endpoints") }}
            </option>
            <option v-for="n in nxEndpoints" :key="n.id" :value="n.id">
              {{ n.display_name || n.hostname }}
              <template v-if="n.presence !== 'online'"> — {{ t("home.offline") }}</template>
            </option>
          </select>
        </label>
        <label>
          <span>{{ t("new.tool") }}</span>
          <select v-model="tool" :disabled="submitting">
            <option value="codex">codex</option>
            <option value="claude">claude</option>
          </select>
        </label>
        <label>
          <span>{{ t("new.profile") }}</span>
          <select v-model="profile" :disabled="submitting || profiles.length === 0">
            <option v-for="p in profiles" :key="`${p.tool}/${p.name}`" :value="p.name">
              {{ p.tool }}/{{ p.name }}
            </option>
          </select>
        </label>
        <label>
          <span>{{ t("new.proxy") }}</span>
          <select v-model="proxy" :disabled="submitting">
            <option value="">{{ t("new.proxy_inherit") }}</option>
            <option v-for="px in proxies" :key="px.name" :value="px.selector">
              {{ px.name }} ({{ px.selector }})
            </option>
          </select>
        </label>
      </template>

      <label v-if="!(target === 'agent' && relayTarget) && engineOptions.length > 0">
        <span>{{ t("new.engine") }}</span>
        <select v-model="engine" :disabled="submitting">
          <option v-for="e in engineOptions" :key="e" :value="e">{{ e }}</option>
        </select>
      </label>
      <p v-if="relayOnlyAgent && !relayTarget" class="new__hint">
        {{ t("new.relay_only_agent") }}
      </p>

      <label>
        <span>{{ t("new.name") }}</span>
        <input v-model="name" :disabled="submitting" :placeholder="t('new.name_placeholder')" />
      </label>

      <label v-if="!(target === 'agent' && relayTarget)">
        <span>{{ t("new.working_dir") }}</span>
        <DirPicker
          v-if="target === 'agent' && agentId"
          v-model="workingDir"
          :endpoint-id="agentId"
          target-type="agent"
          :disabled="submitting"
        />
        <DirPicker
          v-else-if="target === 'nx' && nxId"
          v-model="workingDir"
          :endpoint-id="nxId"
          target-type="nx"
          :disabled="submitting"
        />
        <input v-else v-model="workingDir" :disabled="submitting" placeholder="/home/dragon" />
      </label>

      <p v-if="errorMessage" class="admin-error">{{ errorMessage }}</p>
      <button type="submit" class="new__submit" :disabled="!canSubmit">
        {{ submitting ? t("admin.submitting") : t("new.start") }}
      </button>
    </form>
  </section>
</template>

<style scoped>
.new {
  max-width: 32rem;
}
.new__form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
}
.new__form label {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.85rem;
}
.new__form select,
.new__form input {
  padding: 0.5rem 0.7rem;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  background: var(--color-bg);
  color: var(--color-fg);
  font-family: inherit;
  font-size: 0.95rem;
}
.new__target {
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 0.5rem 0.8rem;
  display: flex;
  gap: 1.2rem;
  flex-direction: row;
  align-items: center;
}
.new__target legend {
  font-size: 0.78rem;
  color: var(--color-fg-dim);
  padding: 0 0.3rem;
}
.new__target label {
  flex-direction: row;
  gap: 0.3rem;
  align-items: center;
}
.new__hint {
  margin: 0;
  font-size: 0.78rem;
  color: var(--color-fg-dim);
}
.new__submit {
  align-self: flex-start;
  padding: 0.55rem 1rem;
  background: var(--color-accent);
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
}
.new__submit:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
</style>
